'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useScopeTree } from '@/hooks/use-scope-tree';
import { api, apiFetch } from '@/lib/utils/api';

/**
 * Stato "applicata / scartata" delle raccomandazioni AI (INB-44).
 *
 * Stava in `localStorage`, per browser. Andava bene finché a guardarlo era la
 * stessa persona sullo stesso dispositivo, e ha smesso di bastare quando il
 * risparmio generato da quelle scelte ha dovuto comparire anche nel portale
 * soci: il browser di un socio non sa nulla di cosa ha adottato
 * l'amministratore. Ora la fonte di verità è il server.
 *
 * Il `localStorage` resta per una ragione sola: **non perdere le risposte già
 * date**. Al primo caricamento con orgId, ciò che c'è in locale viene travasato
 * una volta sul server, che lo accetta solo per le raccomandazioni di cui non sa
 * ancora nulla — così un dispositivo rimasto indietro non può riportare indietro
 * uno stato più recente.
 */

/** Per ogni raccomandazione: `'no'`, oppure la data ISO in cui è stata applicata. */
export type RecFeedback = Record<string, string>;

interface FeedbackRow {
	recId: string;
	answer: string;
	adoptedOn: string | null;
	droppedOn: string | null;
}

function storageKey(tenantId: string): string {
	return `slims:admin-rec-applied:${tenantId}`;
}

function readLocal(tenantId: string | undefined): RecFeedback {
	if (!tenantId || typeof window === 'undefined') return {};
	try {
		const raw = window.localStorage.getItem(storageKey(tenantId));
		return raw ? (JSON.parse(raw) as RecFeedback) : {};
	} catch {
		return {};
	}
}

/**
 * Righe del server → mappa attesa dal pannello.
 *
 * Una raccomandazione adottata e poi revocata torna "non risposta": nel pannello
 * non è più applicata, e mostrarla ancora spuntata contraddirebbe il fatto che
 * ha smesso di produrre risparmio. Lo storico della finestra resta sul server,
 * dove serve al calcolo dei mesi passati.
 */
function toApplied(rows: FeedbackRow[]): RecFeedback {
	const out: RecFeedback = {};
	for (const r of rows) {
		if (r.answer === 'no') out[r.recId] = 'no';
		else if (r.adoptedOn && !r.droppedOn) out[r.recId] = `${r.adoptedOn}T00:00:00`;
	}
	return out;
}

export function useAdminRecFeedback(tenantId: string | undefined) {
	const { leaves } = useScopeTree();
	const contextOrg = leaves.find((s) => s.tenantId === tenantId);
	const mode = contextOrg?.features?.aiSuggestions ?? 'disabled';
	const orgId = mode === 'enabled' ? contextOrg?.orgId : undefined;
	const [applied, setApplied] = useState<RecFeedback>({});
	const imported = useRef<string | null>(null);

	const load = useCallback(async () => {
		if (!orgId) return;
		const rows = await api.get<FeedbackRow[]>('/api/ai-recommendations/feedback', { orgId });
		setApplied(toApplied(Array.isArray(rows) ? rows : []));
	}, [orgId]);

	useEffect(() => {
		if (!orgId) {
			// Senza configurazione selezionata si mostra almeno ciò che il browser
			// ricorda, invece di un pannello vuoto.
			setApplied(mode === 'enabled' ? readLocal(tenantId) : {});
			return;
		}
		let cancelled = false;

		(async () => {
			try {
				const rows = await api.get<FeedbackRow[]>('/api/ai-recommendations/feedback', { orgId });
				const server = Array.isArray(rows) ? rows : [];
				if (cancelled) return;

				// Travaso una tantum per org: solo se il server non conosce già la
				// raccomandazione, e solo una volta per sessione.
				const local = readLocal(tenantId);
				const known = new Set(server.map((r) => r.recId));
				const entries = Object.entries(local)
					.filter(([recId]) => !known.has(recId))
					.map(([recId, value]) => ({
						recId,
						answer: value === 'no' ? ('no' as const) : ('si' as const),
						adoptedOn: value === 'no' ? null : value.slice(0, 10),
					}));

				if (entries.length > 0 && imported.current !== orgId) {
					imported.current = orgId;
					const res = await apiFetch(`/api/ai-recommendations/feedback/import?orgId=${orgId}`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ entries }),
					});
					// Il travaso è riservato agli admin: per un socio fallisce, ed è
					// corretto — le adozioni sono una scelta della configurazione.
					if (res.ok && !cancelled) {
						await load();
						return;
					}
				}
				setApplied(toApplied(server));
			} catch {
				if (!cancelled) setApplied(mode === 'enabled' ? readLocal(tenantId) : {});
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [orgId, tenantId, load, mode]);

	const setFeedback = useCallback(
		async (recId: string, answer: 'si' | 'no') => {
			if (mode === 'demo') {
				setApplied((previous) => ({
					...previous,
					[recId]: answer === 'si' ? new Date().toISOString() : 'no',
				}));
				return;
			}
			if (!orgId) return;
			const previous = applied;
			// Aggiornamento ottimistico: la levetta deve rispondere al click, non al
			// round-trip. In caso di errore si torna esattamente allo stato di prima.
			setApplied({
				...applied,
				[recId]: answer === 'si' ? new Date().toISOString() : 'no',
			});
			try {
				const res = await apiFetch(`/api/ai-recommendations/feedback?orgId=${orgId}`, {
					method: 'PUT',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ recId, answer }),
				});
				if (!res.ok) throw new Error(String(res.status));
				await load();
			} catch {
				setApplied(previous);
				toast.error('Non è stato possibile salvare la risposta');
			}
		},
		[orgId, applied, load, mode],
	);

	const clearFeedback = useCallback(
		async (recId: string) => {
			if (mode === 'demo') {
				setApplied((previous) => {
					const next = { ...previous };
					delete next[recId];
					return next;
				});
				return;
			}
			if (!orgId) return;
			const previous = applied;
			const next = { ...applied };
			delete next[recId];
			setApplied(next);
			try {
				const res = await apiFetch(`/api/ai-recommendations/feedback/${recId}?orgId=${orgId}`, {
					method: 'DELETE',
				});
				if (!res.ok) throw new Error(String(res.status));
				await load();
			} catch {
				setApplied(previous);
				toast.error('Non è stato possibile annullare la risposta');
			}
		},
		[orgId, applied, load, mode],
	);

	return { applied, setFeedback, clearFeedback, refetch: load };
}
